import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { ArrowLeftIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { receiptApi } from '../services/api';
import ReceiptSigner from '../components/ReceiptSigner';

const SignReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        setLoading(true);
        const response = await receiptApi.getById(id);
        setReceipt(response.data);
      } catch (error) {
        console.error('Failed to fetch receipt:', error);
        toast.error('Failed to load receipt');
        navigate('/receipts');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [id, navigate]);

  const handleDownload = async () => {
    try {
      await receiptApi.download(id);
    } catch (error) {
      console.error('Failed to download receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  const handleSigningComplete = () => {
    toast.success('Receipt successfully signed. You can now download it.');
    // Refresh receipt data to get updated signature status
    receiptApi.getById(id).then(response => {
      setReceipt(response.data);
    });
  };

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <Link to="/receipts" className="text-gray-600 hover:text-gray-900 mr-2">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          Sign Receipt
        </h1>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading receipt details...</div>
      ) : receipt ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Receipt details column */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Receipt Details</h2>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Receipt Number</p>
                  <p className="font-medium">{receipt.receiptNo}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Rental Period</p>
                  <p className="font-medium">
                    {formatDate(receipt.periodStart)} to {formatDate(receipt.periodEnd)}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="font-medium">${receipt.totalAmount.toFixed(2)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Payment Date</p>
                  <p className="font-medium">{formatDate(receipt.paymentDate)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <p className="font-medium">{receipt.paymentType}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Language</p>
                  <p className="font-medium">{receipt.language === 'fr' ? 'French' : 'English'}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">
                    {receipt.isSigned ? (
                      <span className="text-green-600">Signed</span>
                    ) : (
                      <span className="text-amber-600">Not signed</span>
                    )}
                  </p>
                </div>
              </div>
              
              {receipt.isSigned && (
                <div className="mt-6">
                  <button
                    onClick={handleDownload}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Download Signed Receipt
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Signing column */}
          <div className="lg:col-span-2">
            {receipt.isSigned ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <svg 
                      className="h-6 w-6 text-green-600" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M5 13l4 4L19 7" 
                      />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Receipt Already Signed</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This receipt has already been signed and cannot be modified.
                    You can download the signed receipt using the button on the left.
                  </p>
                </div>
              </div>
            ) : (
              <ReceiptSigner 
                receipt={receipt} 
                onSigningComplete={handleSigningComplete} 
              />
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">Receipt not found</div>
      )}
    </div>
  );
};

export default SignReceipt;